import {
  AddIcon,
  Button,
  FlexContent,
  InlineOrderEditor,
  ModalDialog,
  TableCell,
  TableHeadCell,
  TableRow,
} from "@rothoblaas/design-system";
import { FC, useContext, useState } from "react";
import Icon from "../../../../components/ui/Icon";
import Table from "../../../../components/ui/Table";
import Visibility from "../../../../components/ui/Visibility";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import type { ExampleAssembly } from "../../../../http/envelopeDesigner/types/exampleAssembly";
import { useAdminEnvelopeDesignerApi } from "../../../../http/envelopeDesigner/useAdminEnvelopeDesigner";
import { getDeleteAction, getEditAction } from "../../../../utils/rowActions";
import ExampleFormDialog from "./ExampleFormDialog";

// The example stratigraphies offered by "From an example" / "Change example".
const ExamplesTab: FC = () => {
  const i18n = useI18n();
  const t = i18n.translation.envelopeDesignerModule.admin;
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminEnvelopeDesignerApi();
  const { data, error } = adminApi.useAdminExampleAssemblies();
  const [editing, setEditing] = useState<ExampleAssembly | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [toDelete, setToDelete] = useState<ExampleAssembly | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const run = async (request: Promise<unknown>) => {
    try {
      await request;
      dispatch(showSuccessNotification(i18n.translation));
      return true;
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
      return false;
    }
  };

  return (
    <FlexContent padding>
      <Table<ExampleAssembly>
        headingActions={
          <Button onClick={() => setIsCreating(true)}>
            <Icon glyph={AddIcon} />
            {i18n.translation.common.add}
          </Button>
        }
        head={
          <TableRow>
            <TableHeadCell align="left">{t.label}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.common.code}</TableHeadCell>
            <TableHeadCell align="left">{t.element}</TableHeadCell>
            <TableHeadCell align="left">{t.structure}</TableHeadCell>
            <TableHeadCell align="left">{t.regions}</TableHeadCell>
            <TableHeadCell align="right">{t.layerCount}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.common.visibility}</TableHeadCell>
            <TableHeadCell align="center">{i18n.translation.common.order}</TableHeadCell>
            <TableHeadCell align="left" />
          </TableRow>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <TableCell>
              {item.label}
              {item.isDefault ? ` (${t.default})` : ""}
            </TableCell>
            <TableCell>{item.code}</TableCell>
            <TableCell>{item.category}</TableCell>
            <TableCell>{item.structure}</TableCell>
            <TableCell>{item.regions.join(", ")}</TableCell>
            <TableCell align="right">{item.layers.length}</TableCell>
            <TableCell>
              <Visibility value={item.visibility} onChange={(visibility) => void run(adminApi.updateExampleAssemblyVisibility(item.id, visibility))} />
            </TableCell>
            <TableCell align="center">
              <InlineOrderEditor order={item.order} onUpdate={(order) => void run(adminApi.updateExampleAssemblyOrder(item.id, order))} />
            </TableCell>
          </>
        )}
        rowActions={(item) => [
          getEditAction(i18n, () => setEditing(item)),
          getDeleteAction(i18n, () => setToDelete(item), true),
        ]}
        onRowClick={(item) => setEditing(item)}
        total={data?.total}
        error={!!error}
      />

      {toDelete && (
        <ModalDialog
          onClose={() => setToDelete(null)}
          title={toDelete.label}
          isSubmitting={isSubmitting}
          buttons={[
            <Button key="no" variant="secondary" onClick={() => setToDelete(null)}>
              {i18n.translation.common.cancel}
            </Button>,
            <Button
              key="yes"
              onClick={async () => {
                setIsSubmitting(true);
                if (await run(adminApi.deleteExampleAssembly(toDelete.id))) setToDelete(null);
                setIsSubmitting(false);
              }}
            >
              {i18n.translation.common.yes}
            </Button>,
          ]}
        >
          {t.deleteExampleQuestion}
        </ModalDialog>
      )}
      {(isCreating || editing) && (
        <ExampleFormDialog
          example={editing}
          onClose={() => {
            setEditing(null);
            setIsCreating(false);
          }}
        />
      )}
    </FlexContent>
  );
};

export default ExamplesTab;
